import { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Lock, ShoppingBag } from "lucide-react";
import { Screen, TopBar, BackButton, Logo, ScrollArea, BottomBar, PrimaryButton } from "@/components/jk/ui";
import { Card, SectionLabel, FullScreenLoader } from "@/components/jk/kit";
import { formatINR } from "@/lib/format";
import { loadRazorpay } from "@/lib/razorpay";
import api from "@/lib/api";

const Field = ({ label, children }) => (
  <div>
    <label className="mb-1.5 block text-[12px] font-semibold uppercase tracking-[0.07em] text-[#666]">{label}</label>
    {children}
  </div>
);

const Input = ({ testid, ...props }) => (
  <input
    data-testid={testid}
    className="w-full rounded-[12px] border border-[#e5e5e5] bg-white px-4 py-3 text-[14px] text-[#111] placeholder-[#bbb] outline-none focus:border-[#111111] transition-colors"
    {...props}
  />
);

const Textarea = ({ testid, ...props }) => (
  <textarea
    data-testid={testid}
    rows={3}
    className="w-full rounded-[12px] border border-[#e5e5e5] bg-white px-4 py-3 text-[14px] text-[#111] placeholder-[#bbb] outline-none focus:border-[#111111] transition-colors resize-none"
    {...props}
  />
);

const ProductBuy = () => {
  const navigate = useNavigate();
  const { productId } = useParams();
  const [searchParams] = useSearchParams();
  const astrologerSlug = searchParams.get("astrologer") || "";

  const [product, setProduct] = useState(null);
  const [astrologerId, setAstrologerId] = useState("");
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    client_name: "",
    client_email: "",
    client_mobile: "",
    client_address: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await api.get("/public/products");
        const found = (data.products || []).find((p) => p.id === productId);
        if (!found) { setLoading(false); return; }
        setProduct(found);
        if (astrologerSlug) {
          const res = await api.get(`/public/astrologer/${astrologerSlug}`);
          setAstrologerId(res.data?.astrologer?.id || "");
        }
      } catch {
        // product or astrologer not found — handled below
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [productId, astrologerSlug]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const validate = () => {
    if (!form.client_name.trim()) return "Full name is required.";
    if (!form.client_email.trim()) return "Email is required.";
    if (!/^\d{10}$/.test(form.client_mobile.trim())) return "Mobile must be exactly 10 digits.";
    if (!form.client_address.trim()) return "Delivery address is required.";
    return null;
  };

  const pay = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    if (!astrologerId) { setError("Could not determine astrologer. Please go back and try again."); return; }
    setPaying(true);
    setError("");
    try {
      const { data } = await api.post("/public/product-orders/create-order", {
        product_id: productId,
        astrologer_id: astrologerId,
        ...form,
        client_mobile: form.client_mobile.trim(),
      });

      if (data.mock) {
        await api.post(`/public/product-orders/${data.order_id}/mock-pay`);
        navigate(`/product-order/${data.order_id}/done`, { replace: true });
        return;
      }

      const ok = await loadRazorpay();
      if (!ok) { setError("Could not load payment gateway. Check your connection."); setPaying(false); return; }

      const rzp = new window.Razorpay({
        key: data.razorpay_key_id,
        order_id: data.razorpay_order_id,
        amount: data.amount,
        currency: "INR",
        name: "JothidaKural",
        description: product.name,
        theme: { color: "#111111" },
        prefill: { name: form.client_name, email: form.client_email, contact: form.client_mobile },
        handler: async (resp) => {
          try {
            await api.post(`/public/product-orders/${data.order_id}/verify-payment`, {
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_signature: resp.razorpay_signature,
            });
            navigate(`/product-order/${data.order_id}/done`, { replace: true });
          } catch {
            setError("Payment could not be verified. If money was deducted, it will be refunded.");
            setPaying(false);
          }
        },
        modal: { ondismiss: () => setPaying(false) },
      });
      rzp.on("payment.failed", () => { setError("Payment failed. Please try again."); setPaying(false); });
      rzp.open();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not start payment. Please try again.");
      setPaying(false);
    }
  };

  if (loading) return <FullScreenLoader />;
  if (!product) return (
    <Screen testid="product-buy-not-found">
      <TopBar left={<BackButton onClick={() => navigate(-1)} />} center={<Logo />} />
      <div className="flex flex-1 flex-col items-center justify-center px-8 text-center">
        <p className="text-[18px] font-bold text-[#111]">Product not found</p>
        <p className="mt-2 text-[14px] text-[#666]">This product is no longer available.</p>
      </div>
    </Screen>
  );

  return (
    <Screen testid="product-buy-screen">
      <TopBar left={<BackButton onClick={() => navigate(-1)} />} center={<Logo />} />

      <ScrollArea className="px-6 pt-2 pb-4">
        {/* Product summary */}
        <Card className="mb-5 flex gap-4 p-4">
          {product.image_url && (
            <img
              src={product.image_url}
              alt={product.name}
              className="h-20 w-20 shrink-0 rounded-[10px] object-cover"
            />
          )}
          <div className="min-w-0">
            <p className="text-[15px] font-bold text-[#111]">{product.name}</p>
            <p className="mt-1 text-[12px] leading-relaxed text-[#666]">{product.description}</p>
            <p className="mt-2 text-[18px] font-bold text-[#111]">{formatINR(product.selling_price_inr)}</p>
          </div>
        </Card>

        <h1 className="mb-4 text-[22px] font-bold tracking-tight text-[#111]">Delivery details</h1>

        <div className="space-y-4">
          <Field label="Full Name">
            <Input
              testid="input-name"
              placeholder="Your full name"
              value={form.client_name}
              onChange={set("client_name")}
            />
          </Field>
          <Field label="Email">
            <Input
              testid="input-email"
              type="email"
              placeholder="you@email.com"
              value={form.client_email}
              onChange={set("client_email")}
            />
          </Field>
          <Field label="Mobile (10 digits)">
            <Input
              testid="input-mobile"
              type="tel"
              inputMode="numeric"
              placeholder="9999999999"
              maxLength={10}
              value={form.client_mobile}
              onChange={set("client_mobile")}
            />
          </Field>
          <Field label="Delivery Address">
            <Textarea
              testid="input-address"
              placeholder="Full address with pincode"
              value={form.client_address}
              onChange={set("client_address")}
            />
          </Field>
        </div>

        <div className="mt-5 flex items-start gap-3 rounded-[14px] bg-[var(--jk-periwinkle)] p-4">
          <Lock size={16} strokeWidth={2} className="mt-0.5 shrink-0 text-[#111]" />
          <p className="text-[12px] leading-relaxed text-[#444]">
            <span className="font-semibold">Secured by Razorpay.</span> Your payment is safe and encrypted.
          </p>
        </div>
      </ScrollArea>

      <BottomBar>
        {error && (
          <p className="mb-3 text-center text-[13px] text-[#d23b2f]" data-testid="product-buy-error">{error}</p>
        )}
        <PrimaryButton
          testid="proceed-to-pay"
          onClick={pay}
          disabled={paying}
          icon={!paying && <ShoppingBag size={16} strokeWidth={2} />}
        >
          {paying ? "Processing…" : `Pay ${formatINR(product.selling_price_inr)}`}
        </PrimaryButton>
      </BottomBar>
    </Screen>
  );
};

export default ProductBuy;
